import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, filter } from 'rxjs';

import { CurrentUserStateService } from './current-user-state.service';

import { UserCurrency, currencySymbolMap } from '../enums/user-currency';
import { UsersApi } from '../../../api/users/users.api';
import { currencyNameMapEn, currencyNameMapFr } from '../data/currency-name-map';
import { DropdownOptionModel } from '../../../Common/Forms/models/dropdown-option.model';
import { LanguageTagEnum } from '../../Localization/enums/language-tag.enum';
@Injectable({
    providedIn: 'root'
})
export class CurrencyConversionService {

    public userCurrency = UserCurrency.USD;
    private currencyRates: any = null;
    private currencyRates$: BehaviorSubject<any> = new BehaviorSubject<any>(this.currencyRates);
    private nameMap = currencyNameMapEn;

    constructor(
        private userStateService: CurrentUserStateService,
        private usersApi: UsersApi
    ){
        this.userStateService.getUserCurrency().pipe(filter(x => !!x)).subscribe(
            currency => this.userCurrency = currency
        );
        this.usersApi.getCurrencyRate().pipe(filter(x => !!x)).subscribe(
            rate => this.setCurrencyRates(rate)
        );
        this.userStateService.getUILanguageTag().subscribe(tag => {
            if (tag === LanguageTagEnum.fr_CA || tag === LanguageTagEnum.fr_FR) {
                this.nameMap = currencyNameMapFr;
            } else {
                this.nameMap = currencyNameMapEn;
            }
        });
    }

    public getCurrencyRates(): Observable<any>{
        return this.currencyRates$;
    }

    public setCurrencyRates(currencyRates): void {
        this.currencyRates = currencyRates;
        this.currencyRates$.next(this.currencyRates);
    }

    /**
     * This is used to convert usd value to other currency and add currency ISO code after the number
     * @param usd usd value
     * @param appendCurrencyCode  whethere need to add currency ISO code after the converted string, default true
     * @param target target currency as UserCurrency type
     * @returns a string composed by the target currency value and the ISO code
     */
    convertCurrency(usd: number, appendCurrencyCode = true, target?: UserCurrency): string {
        if (usd === undefined){
            return undefined;
        }

        let result: string |number = this.convertCurrencyWithoutISOCode(usd, target);
        if (isNaN(result)) {
            return 'N/A';
        }
        if (appendCurrencyCode){
            result = result.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2});
            return this.appendCurrencyCode(result, target);
        } else {
            result = result?.toFixed(2);
            return result;
        }
    }

    convertUserCurrencyToUSD(amount: number): number {
        if (amount === undefined) {
            return amount;
        }

        const targetRate = this.currencyRates?.[this.userCurrency];
        const usdRate = this.currencyRates?.[UserCurrency.USD];
        return amount / usdRate * targetRate;
    }


    getCurrencyRate(currency: UserCurrency): number {
        if (!currency){
            return 1;
        }
        const targetRate = this.currencyRates?.[currency];
        const usdRate = this.currencyRates?.[UserCurrency.USD];
        return usdRate / targetRate;
    }

    convertCurrencyWithoutISOCode(usd: number, target?: UserCurrency): number {
        if (!target){
            target = this.userCurrency;
        }
        if (target === UserCurrency.USD){
            return usd;
        }
        const targetRate = this.currencyRates?.[target];
        const usdRate = this.currencyRates?.[UserCurrency.USD];
        return usd / targetRate * usdRate;
    }

    appendCurrencyCode(source: string | number, target?: UserCurrency): string {
        if (!target){
            target = this.userCurrency;
        }
        const symbol = currencySymbolMap.get(target);
        if (symbol){
            return symbol + source;
        }else{
            return source + ' ' + target;
        }
    }

    getAllCurrencies(): UserCurrency[]{
        const rawCurrency = Object.values(UserCurrency);
        return rawCurrency.map(c => UserCurrency[c]);
    }

    getCurrencyNameMapping(): Map<string, string>{
        return this.nameMap;
    }

    getCurrencyDropdownOptions(): DropdownOptionModel[]{
        return this.getAllCurrencies().map(_currency => new DropdownOptionModel(this.getCurrencyNameMapping().get(_currency), _currency));
    }

}
